/**
 * Recurring Tickets — load-bearing API-layer helpers.
 *
 * These are colocated with the API client because they guard backend bugs at the
 * exact boundary where the payload leaves / the response arrives. Keeping them
 * next to the API functions makes the mitigation impossible to miss.
 */

import type {
  ListRecurringTicketsResponse,
  RecurringTicketListEnvelope,
  UpdateRecurringTicketPayload,
} from "shared";

// ----------------------------------------------------------------------------
// unwrapList — normalize the non-standard list envelope
// ----------------------------------------------------------------------------

/**
 * The recurring-tickets list endpoint returns
 * `{ data: { length, totalCount, totalPages, result[] } }` instead of the DeskDay-standard
 * `{ data: [], pagination: {} }`. This helper normalises it into a shape the UI can
 * consume directly without touching the envelope structure.
 *
 * Pass this into TanStack Query `select` in Phase 2 so the normalisation runs once per
 * response and memoises through react-query's reference stability.
 */
export const unwrapList = <T,>(
  envelope: ListRecurringTicketsResponse
): {
  items: T[];
  totalCount: number;
  totalPages: number;
  length: number;
} => {
  // Defensive: backend has shipped empty/undefined data shapes in the past.
  const data: RecurringTicketListEnvelope<T> | undefined = envelope?.data as
    | RecurringTicketListEnvelope<T>
    | undefined;

  return {
    items: (data?.result ?? []) as T[],
    totalCount: data?.totalCount ?? 0,
    totalPages: data?.totalPages ?? 0,
    length: data?.length ?? 0,
  };
};

// ----------------------------------------------------------------------------
// stripStatusFromPatch — BUG-10 mitigation
// ----------------------------------------------------------------------------

/**
 * BUG-10: PATCH `/recurring-tickets/:id` silently accepts a `status` field instead of
 * rejecting it with 422 (per spec). This helper strips `status` at runtime before the
 * payload leaves the client, so even a drifting caller cannot accidentally mutate
 * status through the update endpoint. Status changes MUST go through
 * `pauseRecurringTicket` / `resumeRecurringTicket`.
 */
export const stripStatusFromPatch = (
  payload: UpdateRecurringTicketPayload
): UpdateRecurringTicketPayload => {
  // The type intentionally excludes `status`, but upstream code can slip it in
  // via a cast. Intercept with a runtime strip.
  const raw = payload as UpdateRecurringTicketPayload & { status?: unknown };
  if (raw && "status" in raw) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { status: _discarded, ...rest } = raw;
    return rest;
  }
  return payload;
};

// ----------------------------------------------------------------------------
// Pause / Resume conflict detection — BUG-11 mitigation
// ----------------------------------------------------------------------------

/**
 * BUG-11: The pause/resume conflict paths return HTTP 200 with a body of
 * `{ success: false, statusCode: 400, message: "Only active recurring tickets can be paused" }`
 * instead of the spec'd HTTP 409. Matching on `statusCode` is unreliable (400 is reused for
 * validation errors). We match on the canonical DeskDay message string instead.
 *
 * The matching is case-insensitive and substring-based so future copy tweaks don't break it.
 */
export const PAUSE_CONFLICT_MESSAGES = [
  "Only active recurring tickets can be paused",
  "Recurring ticket is already paused",
] as const;

export const RESUME_CONFLICT_MESSAGES = [
  "Only paused recurring tickets can be resumed",
  "Recurring ticket is not paused",
] as const;

interface ErrorShape {
  message?: string;
  statusCode?: number;
}

const matchesAny = (
  message: string | undefined,
  patterns: readonly string[]
): boolean => {
  if (!message) return false;
  const lower = message.toLowerCase();
  return patterns.some((p) => lower.includes(p.toLowerCase()));
};

/** BUG-11: returns true if the error body indicates the ticket is already paused. */
export const matchPauseConflict = (
  error: ErrorShape | null | undefined
): boolean => matchesAny(error?.message, PAUSE_CONFLICT_MESSAGES);

/** BUG-11: returns true if the error body indicates the ticket is already active. */
export const matchResumeConflict = (
  error: ErrorShape | null | undefined
): boolean => matchesAny(error?.message, RESUME_CONFLICT_MESSAGES);

/**
 * Convenience matcher used by mutation hooks: returns a discriminated classification
 * so the UI layer can pick the right toast copy.
 */
export type PauseResumeConflictKind = "already_paused" | "already_active" | null;

export const matchPauseResumeConflict = (
  error: ErrorShape | null | undefined
): PauseResumeConflictKind => {
  if (matchPauseConflict(error)) return "already_paused";
  if (matchResumeConflict(error)) return "already_active";
  return null;
};
